import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import TransactionsService from '@/api/transactions.service';
import ChartOfAccountsService from '@/api/chartofaccounts.service';
import { TransactionType } from '@/types/index';
import { AccountType } from "@/types/index"
import { format } from 'date-fns';

interface ChartOfAccount {
  id?: number
  publicId: string
  code: string
  name: string
  type: AccountType
  isActive: boolean
  [key: string]: any // Allow other properties
}

export default function CreateTransaction() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Check if accountId was passed in state
  const initialAccountId = location.state?.accountId ? String(location.state.accountId) : '';
  
  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccount[]>([]);
  const [accountId, setAccountId] = useState(initialAccountId);
  const [amount, setAmount] = useState('');
  const [transactionType, setTransactionType] = useState<TransactionType>('DEBIT');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [transactionDate, setTransactionDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [currency, setCurrency] = useState('USD');
  const [isSaving, setIsSaving] = useState(false);
  
  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchChartOfAccounts = async () => {
      try {
        if (!selectedBusiness) return;
        const response = await ChartOfAccountsService.getAllChartOfAccounts(selectedBusiness.id);
        if (response.data.result === 'success') {
          // Filter to include only active accounts
          const activeAccounts = response.data.chartOfAccounts.filter(
            (account: ChartOfAccount) => account.isActive
          );
          setChartOfAccounts(activeAccounts);
        }
      } catch (error) {
        console.error("Error fetching chart of accounts:", error);
        toast({
          title: 'Error',
          description: 'Failed to load chart of accounts',
          variant: 'destructive',
        });
      }
    };

    fetchChartOfAccounts();
  }, [selectedBusiness, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accountId || !amount || !transactionDate) {
      toast({
        title: 'Required Fields Missing',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Convert amount to cents for storage
     //const amountInCents = Math.round(parseFloat(amount) * 100);
      
      const transactionData = {
        chartOfAccountId: parseInt(accountId),
        transactionType,
        amount: parseFloat(amount),
        currency,
        description: description || undefined,
        reference: reference || undefined,
        referenceType: 'manual',
        transactionDate,
      };
      
      const response = await TransactionsService.createTransaction(transactionData, selectedBusiness.id);
      
      if (response.data.result === 'success') {
        toast({
          title: 'Transaction Created',
          description: 'Transaction has been recorded successfully',
        });
        navigate('/transactions');
      } else {
        toast({
          title: 'Error',
          description: response.data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while creating the transaction',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center mb-6">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          size="sm"
          className="mr-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold">Record New Transaction</h1>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Transaction Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="account">
                      Account <span className="text-red-500">*</span>
                    </Label>
                    <Select value={accountId} onValueChange={setAccountId} required>
                      <SelectTrigger id="account">
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                      <SelectContent>
                        {chartOfAccounts.map((account: any) => (
                          <SelectItem key={account.id} value={account.id.toString()}>
                            <div className="flex items-center">
                              <span className="font-mono text-xs text-muted-foreground mr-2">
                                {account.code}
                              </span>
                              <span>{account.name}</span>
                              <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-700">
                                {account.type}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="transaction-type">
                      Transaction Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={transactionType} onValueChange={(v) => setTransactionType(v as TransactionType)}>
                      <SelectTrigger id="transaction-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DEBIT">DEBIT</SelectItem>
                        <SelectItem value="CREDIT">CREDIT</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      For most accounts, debits increase and credits decrease the account balance
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">
                      Amount <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <span className="text-slate-500">{currency}</span>
                      </div>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-12"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger id="currency">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                        <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                        <SelectItem value="ZWG">ZWG - Zimbabwe Dollar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="transaction-date">
                      Transaction Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="transaction-date"
                      type="date"
                      value={transactionDate}
                      onChange={(e) => setTransactionDate(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="reference">Reference</Label>
                    <Input
                      id="reference"
                      placeholder="e.g., Invoice #INV-001"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional reference number or identifier
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Enter transaction description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Transaction Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Account</span>
                    <span className="font-medium">
                      {accountId ? chartOfAccounts.find((a: any) => a.id.toString() === accountId)?.name || 'Unknown' : '-'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Type</span>
                    <span className="font-medium">{transactionType}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Amount</span>
                    <span className="font-medium">
                      {amount && !isNaN(parseFloat(amount)) 
                        ? `${currency} ${parseFloat(amount).toFixed(2)}` 
                        : '-'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Date</span>
                    <span className="font-medium">{transactionDate || '-'}</span>
                  </div>
                </div>
                
                <div className="bg-slate-50 p-4 rounded-md mt-4">
                  <div className="flex items-start gap-2">
                    <CreditCard className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Accounting Effect</p>
                      {accountId && amount && !isNaN(parseFloat(amount)) ? (
                        <p className="text-sm text-slate-600 mt-1">
                          This will {transactionType === 'DEBIT' ? 'increase' : 'decrease'} the{' '}
                          {chartOfAccounts.find((a: any) => a.id.toString() === accountId)?.type.toLowerCase() || 'account'}{' '}
                          balance by {currency} {parseFloat(amount).toFixed(2)}.
                        </p>
                      ) : (
                        <p className="text-sm text-slate-600 mt-1">
                          Select an account and enter an amount to see the effect.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-end">
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Transaction
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
} 